import { useState } from 'react';

import { datadogLogs } from '@datadog/browser-logs';

import { Button, UploadIcon } from '@motorway/mw-highway-code';

import { useFetch, useReport } from '@motorway/motorway-handshake-components';
import type { ImageUpload, ReportImage } from '@motorway/motorway-handshake-components/types';

import { debouncedAlert } from '../../../helpers/debouncedAlert';
import { ImageThumbnail } from './ImageThumbnail';

import styles from './Images.module.scss';

export const Images = () => {
  const { report } = useReport();
  const { images: reportImages = [] } = report ?? {};

  const [images, setImages] = useState<ImageUpload[]>(
    reportImages
      .reverse()
      .map((image: ReportImage) => ({
        ...image,
        localId: crypto.randomUUID(),
        uploadUrl: { ...{
          ...image,
          status: 'success',
        } },
      })),
  );

  const { deleteImage, getPreSignedUrls } = useFetch();

  const triggerDeleteImage = (data: ImageUpload) => {
    deleteImage({ data });
    setImages(prev => prev.filter(image => image.localId !== data.localId));
  };

  return (
    <section className={styles.component}>
      <label htmlFor="images" className={styles.label}>
        <input
          accept="image/*, video/*"
          hidden
          id="images"
          multiple
          onChange={async (e) => {
            const files = Array.from(e.target.files ?? []);

            const fileArray = [...files.map(
              file => ({
                file,
                localId: crypto.randomUUID(),
                url: URL.createObjectURL(file),
              }),
            )];

            setImages(prev => [...fileArray, ...prev]);

            const { data, success } = await getPreSignedUrls(files);
            // TODO - Patch report on image upload
            if (!success) {
              datadogLogs.logger.error('Image upload failed', { data, files });
              debouncedAlert('An error occurred while uploading your images. Please try again.');
              return;
            }

            const uploadUrls = (data as { results: NonNullable<ImageUpload['uploadUrl']>[] }).results;

            setImages((prev) => {
              const newImages = [...fileArray, ...prev].map((data) => {
                const uploadUrl = uploadUrls.find(url => url.key?.endsWith(data?.file?.name ?? ''));

                return {
                  ...data,
                  uploadUrl,
                };
              });

              const dedupedNewImages = Array.from(
                new Map(newImages.map(item => [item.localId, item])).values(),
              );

              return dedupedNewImages;
            });

            e.target.value = '';
          }}
          name="images"
          type="file"
        />
        <Button
          icon={UploadIcon}
          label="Upload media"
          fullWidth
          reverse
          showLabel
          size="medium"
          type="button"
          variant="secondary"
        />
      </label>
      <section className={styles.thumbnails}>
        {images.map(data => (
          <ImageThumbnail key={data.localId} {...{ data, deleteImage: triggerDeleteImage }} />
        ))}
      </section>
    </section>
  );
};
